import { api } from "@/lib/api";
import type {
  GuestList,
  CreateGuestListDto,
  UpdateGuestListDto,
  GuestListPermission,
  BulkAddPermissionsDto,
  BulkUpdatePermissionsDto,
  BulkRemovePermissionsDto,
  BulkPermissionsResult,
} from "@/types/guest";

class GuestListService {
  /**
   * Get all guest lists for an event
   */
  async getByEvent(
    eventId: string,
    options?: { includeTimestamps?: boolean },
  ): Promise<GuestList[]> {
    const params = new URLSearchParams({ eventId });
    if (options?.includeTimestamps) params.append("include", "timestamps");
    return api.get<GuestList[]>(`/guest-lists?${params}`);
  }

  /**
   * Get a single guest list by ID
   */
  async getById(id: string): Promise<GuestList> {
    return api.get<GuestList>(`/guest-lists/${id}`);
  }

  /**
   * Create a new guest list
   */
  async create(data: CreateGuestListDto): Promise<GuestList> {
    return api.post<GuestList>("/guest-lists", data);
  }

  /**
   * Update a guest list
   */
  async update(id: string, data: UpdateGuestListDto): Promise<GuestList> {
    return api.patch<GuestList>(`/guest-lists/${id}`, data);
  }

  /**
   * Delete a guest list
   */
  async delete(id: string): Promise<GuestList> {
    return api.delete<GuestList>(`/guest-lists/${id}`);
  }

  /**
   * Get permissions for a guest list
   */
  async getPermissions(id: string): Promise<{
    permissions: GuestListPermission[];
    total: number;
  }> {
    return api.get<{ permissions: GuestListPermission[]; total: number }>(
      `/guest-lists/${id}/permissions`,
    );
  }

  /**
   * Add permissions to a guest list (Bulk)
   */
  async addPermissions(
    id: string,
    data: BulkAddPermissionsDto,
  ): Promise<BulkPermissionsResult> {
    return api.post<BulkPermissionsResult>(
      `/guest-lists/${id}/permissions/bulk`,
      data,
    );
  }

  /**
   * Update permissions for a guest list (Bulk)
   */
  async updatePermissions(
    id: string,
    data: BulkUpdatePermissionsDto,
  ): Promise<BulkPermissionsResult> {
    return api.put<BulkPermissionsResult>(
      `/guest-lists/${id}/permissions/bulk`,
      data,
    );
  }

  /**
   * Remove permissions from a guest list (Bulk)
   */
  async removePermissions(
    id: string,
    data: BulkRemovePermissionsDto,
  ): Promise<BulkPermissionsResult> {
    return api.delete<BulkPermissionsResult>(
      `/guest-lists/${id}/permissions/bulk`,
      data,
    );
  }
}

export const guestListService = new GuestListService();
